import { ChatSession, ProviderConfig } from "../../core/types";
import {
  getProviders,
  getSession,
  saveSession,
  closeSession,
  genId,
  getDefaultMaxContextTokens,
  isModelSpecificMaxContext,
  getDefaultMaxOutputTokens,
  getStoryBibles,
  getStoryBible,
} from "../../core/storage";
import { listModels } from "../../core/adapters";
import { getStrings, Strings } from "../../core/i18n";
import { exportSessionToFile } from "../exportChat";
import { isWordAvailable } from "../word";
import { escapeHtml, escapeAttr, wireInfoIcons, labelWithInfo } from "./domUtils";
import { createModelCombobox, ModelComboboxHandle } from "./modelCombobox";
import { ChatHandlers, renderChat, getContentMode, setContentMode } from "./chat";
import { storyBibleFieldHtml, renderStoryBibleEditor } from "./storyBible";

// The session-switcher chrome and the small standalone screens it opens (start a new chat, edit
// a chat's name/provider/model/temperature/context/system prompt) — split out of chat.ts
// (2026-08-10 code-quality pass, see CLAUDE.md) as the part of that file with no shared closure
// state of its own. The one piece of state these do share with chat.ts — which session/screen is
// currently showing — goes through chat.ts's getContentMode()/setContentMode() rather than a
// module-level variable of its own, since chat.ts's renderChat() also reads and writes that same
// state to decide what to render.

// Session switching. A dropdown of open sessions (rather than a tab strip) —
// simpler, no scroll/overflow handling needed regardless of how many chats are open.
export function renderSessionSwitcher(
  switcherEl: HTMLElement,
  providers: ProviderConfig[],
  openSessions: ChatSession[],
  activeSessionId: string | null,
  handlers: ChatHandlers,
  container: HTMLElement
): void {
  // Looked up in memory from the already-fetched `providers` list, not via getProvider() —
  // that would re-read and re-parse the entire providers blob from localStorage once per open
  // session, on every render of this switcher (which is most clicks in the pane).
  const s = getStrings();
  const providersById = new Map(providers.map((p) => [p.id, p]));
  // "${provider name}: ${chat name}" plus, when this chat has a bound Story Bible,
  // " | ${bible name}" — explicit request (2026-08-12), reinstating the provider-name prefix
  // that had been dropped earlier per a different explicit request, now alongside the Story
  // Bible suffix added since. Dangling storyBibleId (its bible was deleted — see storage.ts's
  // deleteStoryBible) resolves to undefined here, same as a dangling providerId, so either half
  // just falls back on its own (unknown provider label / plain chat name) rather than the whole
  // option failing to render.
  const optionsHtml = openSessions
    .map((session) => {
      const provider = providersById.get(session.providerId);
      const providerName = provider ? provider.name : s.history.unknownProvider;
      const bible = session.storyBibleId ? getStoryBible(session.storyBibleId) : undefined;
      const label = bible
        ? `${providerName}: ${session.title} | ${bible.name}`
        : `${providerName}: ${session.title}`;
      return `<option value="${session.id}"${session.id === activeSessionId ? " selected" : ""}>${escapeHtml(label)}</option>`;
    })
    .join("");

  const activeSession = openSessions.find((s) => s.id === activeSessionId);

  // Chat-scoped Export and app nav (History/Providers/Story Bible) share one row under the
  // switcher, separated by spacing alone (.nav-cluster's flex gap) rather than a separator glyph
  // — tried a middot divider first, reverted per feedback ("I dont like that dot"). Export only
  // makes sense with an active session to act on; History/Providers/Story Bible don't depend on
  // one (Story Bible is pure list/manage — see storyBible.ts's renderStoryBibleList — so it's
  // grouped with the other always-present items, not Export). The standalone "Edit" link that
  // used to sit here was merged into the pencil button (session-cluster, below) 2026-08-11 —
  // having both a pencil for rename and a separate "Edit" link for provider/model/temperature was
  // two places to edit the same chat; the pencil now opens the full Edit session screen (name
  // included) instead, which is also the only place a chat's Story Bible binding is changed —
  // "Story Bible" here never reflects or edits that binding itself, per explicit feedback the
  // label stays static regardless of what's bound.
  const actionItems: { id: string; label: string }[] = [
    ...(activeSession ? [{ id: "export-chat-btn", label: s.common.export }] : []),
    { id: "history-link", label: s.sessionSwitcher.navHistory },
    { id: "manage-providers-link", label: s.sessionSwitcher.navProviders },
    { id: "story-bible-link", label: s.sessionSwitcher.navStoryBible },
  ];
  const actionsHtml = actionItems
    .map((item) => `<button type="button" class="link-btn" id="${item.id}">${item.label}</button>`)
    .join("");

  // Settings gear — same row as Export/History/Providers/Story Bible above, pushed to the row's
  // own right edge (`.nav-cluster-settings-btn`'s margin-left: auto) rather than joining the
  // left-aligned text-link group, so it reads as a distinct "app settings" action rather than one
  // more item in that list. Icon-only (no text label). Uses `.icon-btn.ghost` — same pale grey
  // (#616161) as the pencil/+/× — per explicit feedback settling on grey after a blue
  // (`.icon-btn.solid`) attempt was tried and reverted. Deliberately still **not** the `&#9881;`
  // HTML entity (unlike the pencil's `&#9998;`) — that glyph rendered as a thin ring of spokes
  // ("a boat steering wheel", per direct feedback) rather than a filled gear, and didn't reliably
  // pick up the CSS `color` at all in at least one render. A plain inline SVG (Material Design's
  // standard "settings" gear path, `fill="currentColor"`) guarantees an actual solid gear shape
  // that reliably tracks whatever color it's given, ghost grey included.
  const settingsGearSvg =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    // The gear outline (first "M19.14..." subpath) is Material Design's standard "settings"
    // icon, unchanged. The second subpath is the hollow center hole — a circle radius 3.6
    // (cx=12,cy=12) in the original icon; shrunk to radius 2.4 here per direct feedback that
    // the hole read too large/hollow. Both subpaths share one nonzero-fill-rule `d`, so the
    // circle's winding direction (same relative curve structure as the original, just scaled)
    // has to stay a *subtraction* from the outer shape, not an additional filled shape.
    '<path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,14.4c-1.33,0-2.4-1.33-2.4-2.4s1.33-2.4,2.4-2.4s2.4,1.33,2.4,2.4S13.33,14.4,12,14.4z"/>' +
    "</svg>";
  const settingsBtnHtml = `<button type="button" class="icon-btn ghost nav-cluster-settings-btn" id="settings-link" title="${s.sessionSwitcher.settingsIconTitle}" aria-label="${s.sessionSwitcher.settingsIconAria}">${settingsGearSvg}</button>`;

  // Web-only home link, back to index.html — sits to the left of the session dropdown, on the
  // same row, per explicit request. Gated on !isWordAvailable() rather than living in
  // standalone.html's own static chrome (an earlier attempt, reverted): explicitly requested at
  // this exact position, level with the dropdown, not a separate row elsewhere on the page. This
  // is the one spot in sessionSwitcher.ts/chat.ts that needs a runtime Word/Web
  // check for something Word must never show (everything else shared between the two either
  // applies to both or is naturally absent in Web, like the selection chip — see word.ts's
  // isWordAvailable and chat.ts's DocumentSelectionChanged guard for that other case). Only
  // rendered alongside .session-cluster (i.e. only when at least one chat is open) — there's no
  // dropdown to sit next to otherwise.
  const homeBtnHtml = !isWordAvailable()
    ? `<a href="index.html" class="icon-btn ghost session-home-btn" id="session-home-btn" title="${s.sessionSwitcher.homeTitle}" aria-label="${s.sessionSwitcher.homeTitle}">` +
      // Same 20x20 icon in a 28x28 box as the legal pages' own .home-icon (privacy/terms/
      // support/data-storage.html) — .session-home-btn (taskpane.css) overrides just the box
      // size on top of .icon-btn.ghost's shared color/hover, per explicit request that this
      // match those pages' size rather than the smaller 15px this row's other icons use.
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M3 11.5 12 4l9 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />' +
      '<path d="M5.5 10v9a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />' +
      '<path d="M10 20v-5h4v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />' +
      "</svg></a>"
    : "";

  // Session controls (switch/new/edit/close — everything that acts on or adds to the open
  // session list) sit on top; the actions row above sits right below it. "+" lives in
  // session-cluster rather than the actions row so it reads as "add to this row" next to the
  // thing it adds to, and matches the pencil/× glyphs' ghost styling/size (icon-btn.ghost)
  // instead of standing out as the one filled/primary button in the row.
  switcherEl.innerHTML = `
    ${
      openSessions.length > 0
        ? `<div class="session-cluster-row">
             ${homeBtnHtml}
             <div class="session-cluster">
               <select id="session-select" aria-label="${s.sessionSwitcher.switchChatAria}">${optionsHtml}</select>
               <button type="button" class="icon-btn ghost" id="new-session-btn" title="${s.sessionSwitcher.newChatTitle}" aria-label="${s.sessionSwitcher.newChatAria}">+</button>
               <button type="button" class="icon-btn ghost" id="edit-session-btn" title="${s.sessionSwitcher.editChatIconTitle}" aria-label="${s.sessionSwitcher.editChatIconAria}">&#9998;</button>
               <span class="delete-confirm-wrap session-close-confirm-wrap">
                 <button type="button" class="icon-btn ghost" id="close-session-btn" data-action="toggle-close-confirm" title="${s.sessionSwitcher.deleteChatIconTitle}" aria-label="${s.sessionSwitcher.deleteChatIconAria}">&times;</button>
                 <div class="delete-confirm-popup">
                   <p>${s.sessionSwitcher.deleteChatConfirmText}</p>
                   <div class="delete-confirm-popup-actions">
                     <button type="button" class="danger-btn" data-action="confirm-close-session">${s.common.yesDelete}</button>
                     <button type="button" class="secondary-btn" data-action="cancel-close-session">${s.common.cancel}</button>
                   </div>
                 </div>
               </span>
             </div>
           </div>`
        : ""
    }
    <div class="nav-cluster">${actionsHtml}${settingsBtnHtml}</div>
  `;

  switcherEl.querySelector("#session-select")?.addEventListener("change", (e) => {
    setContentMode({ kind: "session", sessionId: (e.target as HTMLSelectElement).value });
    renderChat(container, handlers);
  });

  // Same floating-popup confirm pattern as chat.ts's message-delete X (wireDeleteButtons) —
  // toggled open/closed via a class, closed by a click anywhere outside the wrap. Added
  // 2026-08-13 per explicit request, so deleting a chat gets the same confirmation step deleting
  // a reply already has, rather than acting immediately on a single click.
  const closeConfirmWrap = switcherEl.querySelector(".session-close-confirm-wrap") as HTMLElement;
  if (closeConfirmWrap) {
    function closeOnOutsideClick(e: MouseEvent): void {
      if (!closeConfirmWrap.contains(e.target as Node)) {
        closeConfirmWrap.classList.remove("is-open");
        document.removeEventListener("click", closeOnOutsideClick);
      }
    }

    closeConfirmWrap
      .querySelector('[data-action="toggle-close-confirm"]')
      ?.addEventListener("click", (e) => {
        // Otherwise this same click immediately reaches the document listener added just below
        // and closes the popup it was meant to open.
        e.stopPropagation();
        const isOpen = closeConfirmWrap.classList.toggle("is-open");
        if (isOpen) {
          document.addEventListener("click", closeOnOutsideClick);
        } else {
          document.removeEventListener("click", closeOnOutsideClick);
        }
      });

    closeConfirmWrap
      .querySelector('[data-action="cancel-close-session"]')
      ?.addEventListener("click", () => {
        closeConfirmWrap.classList.remove("is-open");
        document.removeEventListener("click", closeOnOutsideClick);
      });

    closeConfirmWrap
      .querySelector('[data-action="confirm-close-session"]')
      ?.addEventListener("click", () => {
        document.removeEventListener("click", closeOnOutsideClick);
        const select = switcherEl.querySelector("#session-select") as HTMLSelectElement | null;
        const id = select?.value;
        if (!id) return;
        closeSession(id);
        const mode = getContentMode();
        if (mode?.kind === "session" && mode.sessionId === id) {
          setContentMode(null);
        }
        renderChat(container, handlers);
      });
  }

  switcherEl.querySelector("#new-session-btn")?.addEventListener("click", () => {
    setContentMode({ kind: "new-session" });
    renderChat(container, handlers);
  });

  if (activeSession) {
    const provider = providersById.get(activeSession.providerId);
    switcherEl.querySelector("#edit-session-btn")?.addEventListener("click", () => {
      const contentEl = container.querySelector("#chat-content") as HTMLElement | null;
      if (contentEl) renderEditSessionForm(contentEl, activeSession, handlers, container);
    });
    switcherEl.querySelector("#export-chat-btn")?.addEventListener("click", () => {
      exportSessionToFile(activeSession, provider ? provider.name : "Unknown provider");
    });
  }

  switcherEl.querySelector("#history-link")?.addEventListener("click", handlers.onOpenHistory);
  switcherEl
    .querySelector("#manage-providers-link")
    ?.addEventListener("click", handlers.onManageProviders);
  switcherEl
    .querySelector("#story-bible-link")
    ?.addEventListener("click", handlers.onManageStoryBibles);
  switcherEl.querySelector("#settings-link")?.addEventListener("click", handlers.onOpenSettings);
}

// The three states Temperature/Max context/System prompt can each be in — see ChatSession's own
// comments on why each needs three, not two. "inherit"/"clear" are reachable via the clickable
// links that replace "(optional)" in each of those three labels (overrideModeLinks below); typing
// a real value flips a field to "override" automatically, and manually clearing it back to blank
// flips it to "clear" (not back to "inherit") — deliberate, per explicit request: backspacing a
// System prompt you'd customized should mean "no system prompt for this chat", not silently
// fall back to whatever the provider has. Only "inherit" is reachable by clicking, never by
// typing — it's the one state that can't be expressed just by what's in the box.
type OverrideMode = "inherit" | "override" | "clear";
type OverrideField = "temperature" | "maxContext" | "systemPrompt";

function temperatureMode(session: ChatSession): OverrideMode {
  if (session.temperature === null) return "clear";
  return session.temperature === undefined ? "inherit" : "override";
}

function maxContextMode(session: ChatSession): OverrideMode {
  if (session.maxContextTokens === null) return "clear";
  return session.maxContextTokens === undefined ? "inherit" : "override";
}

function systemPromptMode(session: ChatSession): OverrideMode {
  if (session.systemPrompt === undefined) return "inherit";
  return session.systemPrompt === "" ? "clear" : "override";
}

// Replaces "(optional)" in a label with two clickable words instead of plain explanatory text —
// per explicit request, so switching between "follow the provider" and "explicitly skip it too"
// is a direct action rather than something you have to already know (type-then-delete used to be
// the only way to get back to blank, and it only ever meant one thing). data-field/data-mode read
// by createOverrideFieldsController below; .is-active-mode (added there too) highlights whichever
// currently applies — neither is highlighted while in "override" mode, since override is just
// "whatever's actually typed", not a link-driven state.
function overrideModeLinks(field: OverrideField): string {
  const s = getStrings();
  return (
    `<span class="override-mode-links">(` +
    `<button type="button" class="link-btn override-mode-link" data-field="${field}" data-mode="inherit">${s.sessionSwitcher.inherit}</button>` +
    ` / ` +
    `<button type="button" class="link-btn override-mode-link" data-field="${field}" data-mode="clear">${s.sessionSwitcher.clear}</button>` +
    `)</span>`
  );
}

// The Temperature/Max context/Max reply length/System prompt field block — shared by Edit
// session and Start a new chat (2026-08-11, made identical field sets per explicit request; a
// brand-new chat previously only had Temperature, so setting a system prompt or a context
// override meant create-then-immediately-edit). `idPrefix` keeps each screen's element ids
// distinct ("edit-session"/"new-session") so both can safely use `#${idPrefix}-...` lookups
// without colliding — the two are never on screen at once, but distinct ids still make each
// screen's DOM unambiguous to read/debug independently. `values` seeds the visible inputs: an
// existing session's own values for Edit session, or nothing (every field starts blank/inherit)
// for a brand-new one.
function overrideFieldsHtml(
  idPrefix: string,
  values: {
    temperature?: number | null;
    maxContextTokens?: number | null;
    maxOutputTokens?: number;
    systemPrompt?: string;
  }
): string {
  const s = getStrings();
  return `
    <label>${labelWithInfo(
      `${s.sessionSwitcher.temperature} ${overrideModeLinks("temperature")}`,
      s.sessionSwitcher.temperatureInfo
    )}
      <input
        type="number"
        id="${idPrefix}-temperature"
        min="0"
        max="2"
        step="0.1"
        value="${typeof values.temperature === "number" ? values.temperature : ""}"
      />
    </label>
    <label>${labelWithInfo(
      `${s.sessionSwitcher.maxContext} ${overrideModeLinks("maxContext")}`,
      s.sessionSwitcher.maxContextInfo
    )}
      <input
        type="number"
        id="${idPrefix}-max-context"
        min="1"
        step="1"
        value="${typeof values.maxContextTokens === "number" ? values.maxContextTokens : ""}"
      />
    </label>
    <label id="${idPrefix}-max-output-label">${labelWithInfo(
      s.sessionSwitcher.maxOutput,
      s.sessionSwitcher.maxOutputInfo
    )}
      <input
        type="number"
        id="${idPrefix}-max-output"
        min="1"
        step="1"
        value="${values.maxOutputTokens !== undefined ? values.maxOutputTokens : ""}"
      />
    </label>
    <label>${labelWithInfo(
      `${s.sessionSwitcher.systemPrompt} ${overrideModeLinks("systemPrompt")}`,
      s.sessionSwitcher.systemPromptInfo
    )}
      <textarea id="${idPrefix}-system-prompt" rows="3">${values.systemPrompt ? escapeHtml(values.systemPrompt) : ""}</textarea>
    </label>`;
}

interface OverrideFieldElements {
  temperatureInput: HTMLInputElement;
  maxContextInput: HTMLInputElement;
  maxOutputInput: HTMLInputElement;
  maxOutputLabel: HTMLElement;
  systemPromptInput: HTMLTextAreaElement;
}

// Pairs with overrideFieldsHtml's `idPrefix` — queries back the elements it just wrote.
function queryOverrideFieldElements(
  contentEl: HTMLElement,
  idPrefix: string
): OverrideFieldElements {
  return {
    temperatureInput: contentEl.querySelector(`#${idPrefix}-temperature`) as HTMLInputElement,
    maxContextInput: contentEl.querySelector(`#${idPrefix}-max-context`) as HTMLInputElement,
    maxOutputInput: contentEl.querySelector(`#${idPrefix}-max-output`) as HTMLInputElement,
    maxOutputLabel: contentEl.querySelector(`#${idPrefix}-max-output-label`) as HTMLElement,
    systemPromptInput: contentEl.querySelector(`#${idPrefix}-system-prompt`) as HTMLTextAreaElement,
  };
}

interface OverrideFieldsController {
  // Live, mutable — read directly by each caller's own Save handler (which still owns
  // validation/persistence itself, since that differs enough between "mutate an existing
  // session" and "construct a new one" that sharing it wasn't worth the extra indirection).
  modes: { temperature: OverrideMode; maxContext: OverrideMode; systemPrompt: OverrideMode };
  updatePlaceholders(provider: ProviderConfig): void;
  applyProviderSwitchRules(provider: ProviderConfig): void;
}

// The inherit/override/clear machinery shared by Edit session and Start a new chat: mode
// tracking, the inherit/clear link clicks, typed-vs-cleared input detection, placeholder
// computation, and the three different provider-switch behaviors (Temperature/System prompt
// untouched, Max context clamped, Max reply length reset — see applyProviderSwitchRules' own
// comment for why each differs). `deps` covers the handful of things only the caller knows: which
// provider is currently selected, the live model combobox (reassigned on every provider switch,
// hence a getter rather than a plain reference), and how to clear a shown error.
function createOverrideFieldsController(
  contentEl: HTMLElement,
  elements: OverrideFieldElements,
  initialModes: OverrideFieldsController["modes"],
  deps: {
    getModelCombobox: () => ModelComboboxHandle | undefined;
    getCurrentProvider: () => ProviderConfig | undefined;
    hideError: () => void;
  }
): OverrideFieldsController {
  const { temperatureInput, maxContextInput, maxOutputInput, maxOutputLabel, systemPromptInput } =
    elements;
  const modes: OverrideFieldsController["modes"] = { ...initialModes };

  function refreshModeLinkHighlight(field: OverrideField): void {
    contentEl
      .querySelectorAll<HTMLButtonElement>(`.override-mode-link[data-field="${field}"]`)
      .forEach((btn) => {
        btn.classList.toggle("is-active-mode", btn.dataset.mode === modes[field]);
      });
  }

  // Model-only max context (bypassing the provider's own maxContextTokens even if it has one) —
  // shared by Max context's "clear" placeholder and the provider-switch clamp check below, both
  // of which need the *real* per-model number rather than whatever the provider happens to have
  // configured.
  function modelOnlyMaxContext(provider: ProviderConfig): number {
    const modelCombobox = deps.getModelCombobox();
    const model = modelCombobox?.getValue();
    return getDefaultMaxContextTokens(provider, {
      model,
      liveContextTokens: model ? modelCombobox?.getContextTokensFor(model) : undefined,
    });
  }

  // Whether modelOnlyMaxContext's number above actually came from this specific model (live or
  // name-pattern) rather than the generic conservative floor — same distinction and same reason
  // providerForm.ts's own Max context placeholder makes (see isModelSpecificMaxContext,
  // storage.ts): "Model default" overstates a number that isn't really tied to the model at all.
  function isModelOnlyMaxContextModelSpecific(provider: ProviderConfig): boolean {
    const modelCombobox = deps.getModelCombobox();
    const model = modelCombobox?.getValue();
    return isModelSpecificMaxContext(provider, {
      model,
      liveContextTokens: model ? modelCombobox?.getContextTokensFor(model) : undefined,
    });
  }

  // Same idea as modelOnlyMaxContext above, for Max reply length: the live per-model number if
  // the combobox has fetched one, else the flat DEFAULT_ANTHROPIC_MAX_TOKENS fallback — mirrors
  // providerForm.ts's own updateMaxOutputPlaceholder so this field's placeholder doesn't go stale
  // the same way that one used to (fixed 2026-08-13).
  function modelOnlyMaxOutput(): number {
    const modelCombobox = deps.getModelCombobox();
    const model = modelCombobox?.getValue();
    return getDefaultMaxOutputTokens({
      liveMaxOutputTokens: model ? modelCombobox?.getMaxOutputTokensFor(model) : undefined,
    });
  }

  // Every placeholder here shows what the field currently resolves to if left blank — same
  // "placeholder previews the effective default" convention providerForm.ts uses for a
  // provider's own fields, just one level down: what THIS chat would use from ITS provider
  // (inherit) or straight from the model, bypassing the provider (clear).
  function updatePlaceholders(provider: ProviderConfig): void {
    const s = getStrings();
    temperatureInput.placeholder =
      modes.temperature === "clear"
        ? s.sessionSwitcher.modelDefault
        : provider.temperature !== undefined
          ? s.sessionSwitcher.providerDefault(String(provider.temperature))
          : s.sessionSwitcher.providerDefaultPlain;

    maxContextInput.placeholder =
      modes.maxContext === "clear"
        ? `${isModelOnlyMaxContextModelSpecific(provider) ? s.sessionSwitcher.modelDefault : s.sessionSwitcher.genericDefault}: ${modelOnlyMaxContext(provider).toLocaleString()}`
        : s.sessionSwitcher.providerDefault(
            (provider.maxContextTokens ?? modelOnlyMaxContext(provider)).toLocaleString()
          );

    maxOutputInput.placeholder = s.sessionSwitcher.providerDefault(
      (provider.maxOutputTokens ?? modelOnlyMaxOutput()).toLocaleString()
    );
    // Same reasoning as providerForm.ts's updateMaxOutputTokensVisibility — only Anthropic
    // requires an explicit reply-length cap, so showing this for other adapters would suggest it
    // does something there too.
    maxOutputLabel.style.display = provider.adapterType === "anthropic" ? "" : "none";

    if (modes.systemPrompt === "clear") {
      systemPromptInput.placeholder = s.sessionSwitcher.noSystemPrompt;
    } else {
      const preview = provider.systemPrompt
        ? provider.systemPrompt.length > 60
          ? `${provider.systemPrompt.slice(0, 60)}…`
          : provider.systemPrompt
        : s.sessionSwitcher.none;
      systemPromptInput.placeholder = s.sessionSwitcher.providerDefault(preview);
    }
  }

  function applyFieldMode(
    field: OverrideField,
    mode: "inherit" | "clear",
    input: HTMLInputElement | HTMLTextAreaElement,
    provider: ProviderConfig
  ): void {
    modes[field] = mode;
    input.value = "";
    refreshModeLinkHighlight(field);
    updatePlaceholders(provider);
  }

  contentEl.querySelectorAll<HTMLButtonElement>(".override-mode-link").forEach((btn) => {
    const field = btn.dataset.field as OverrideField;
    const mode = btn.dataset.mode as "inherit" | "clear";
    const input =
      field === "temperature"
        ? temperatureInput
        : field === "maxContext"
          ? maxContextInput
          : systemPromptInput;
    btn.addEventListener("click", () => {
      deps.hideError();
      const provider = deps.getCurrentProvider();
      if (provider) applyFieldMode(field, mode, input, provider);
    });
  });

  // Typing a real value always means "override"; clearing it back to blank by hand means
  // "clear", not "inherit" — see OverrideMode's own comment for why that's deliberate. Only the
  // explicit links above ever produce "inherit".
  function wireTypedOverride(
    field: OverrideField,
    input: HTMLInputElement | HTMLTextAreaElement
  ): void {
    input.addEventListener("input", () => {
      modes[field] = input.value.trim() === "" ? "clear" : "override";
      refreshModeLinkHighlight(field);
    });
  }
  wireTypedOverride("temperature", temperatureInput);
  wireTypedOverride("maxContext", maxContextInput);
  wireTypedOverride("systemPrompt", systemPromptInput);

  refreshModeLinkHighlight("temperature");
  refreshModeLinkHighlight("maxContext");
  refreshModeLinkHighlight("systemPrompt");

  // Provider-switch behavior — three different rules, per explicit spec:
  // - Temperature/System prompt: an override is this chat's own and survives a provider switch
  //   completely untouched (nothing to do here beyond the placeholder refresh already happening
  //   in updatePlaceholders, called separately by each caller's own mountComboboxFor).
  // - Max context: an override is checked against the *new* provider's real limit
  //   (modelOnlyMaxContext, matching how "Model default" is computed for Clear) — kept as-is if
  //   it still fits, brought down to that limit if it doesn't. A provider you're not overriding
  //   for (inherit/clear) needs no action; its placeholder already tracks the new provider.
  // - Max reply length: has no clamp target worth checking against (its provider-level "default"
  //   isn't a real ceiling the way context's is — see ChatSession.maxOutputTokens's own comment),
  //   so an override here is simply discarded back to inherit on any provider switch, matching
  //   how rarely this field gets touched in the first place.
  function applyProviderSwitchRules(provider: ProviderConfig): void {
    if (modes.maxContext === "override") {
      const raw = maxContextInput.value.trim();
      const current = raw ? Number(raw) : NaN;
      if (!Number.isNaN(current)) {
        const newLimit = modelOnlyMaxContext(provider);
        if (current > newLimit) {
          maxContextInput.value = String(newLimit);
        }
      }
    }
    if (maxOutputInput.value.trim() !== "") {
      maxOutputInput.value = "";
    }
  }

  return { modes, updatePlaceholders, applyProviderSwitchRules };
}

interface ParsedOverrideFields {
  model: string;
  temperature: number | null | undefined;
  maxContextTokens: number | null | undefined;
  maxOutputTokens: number | undefined;
  systemPrompt: string | undefined;
}

// Shared by both renderNewSessionSetup's and renderEditSessionForm's Save handlers — the two
// used to each carry their own ~70-line copy of this exact validation (model presence/known-ness,
// Temperature's and Max context's inherit/override/clear three-state resolution, Max output's
// Anthropic-only raw-value presence, System prompt's own three-state resolution), which could
// silently drift out of sync on a future validation-rule change. Returns null having already
// called showError() on the first failure — same early-exit shape each inline copy already had,
// just centralized. Caller-specific concerns (multi-window staleness guard, building vs. mutating
// a ChatSession, providerChanges logging) stay in each handler, since those genuinely differ.
function parseOverrideFields(
  modelCombobox: ModelComboboxHandle,
  controller: OverrideFieldsController,
  fields: OverrideFieldElements,
  provider: ProviderConfig,
  s: Strings,
  showError: (message: string) => void
): ParsedOverrideFields | null {
  const model = modelCombobox.getValue();
  if (!model) {
    showError(s.sessionSwitcher.enterOrSelectModel);
    return null;
  }
  if (modelCombobox.hasFetchedModels() && !modelCombobox.isKnownModel(model)) {
    showError(s.sessionSwitcher.modelNotInList(model));
    return null;
  }

  let temperature: number | null | undefined;
  if (controller.modes.temperature === "inherit") {
    temperature = undefined;
  } else if (controller.modes.temperature === "clear") {
    temperature = null;
  } else {
    const parsed = Number(fields.temperatureInput.value.trim());
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 2) {
      showError(s.sessionSwitcher.temperatureRange);
      return null;
    }
    temperature = parsed;
  }

  let maxContextTokens: number | null | undefined;
  if (controller.modes.maxContext === "inherit") {
    maxContextTokens = undefined;
  } else if (controller.modes.maxContext === "clear") {
    maxContextTokens = null;
  } else {
    const parsed = Number(fields.maxContextInput.value.trim());
    if (!Number.isInteger(parsed) || parsed < 1) {
      showError(s.sessionSwitcher.maxContextPositive);
      return null;
    }
    maxContextTokens = parsed;
  }

  // Only meaningful (and only shown, see updatePlaceholders) for Anthropic providers — ignored
  // here for any other adapter type even if a stale value is still sitting in the hidden field
  // from a since-changed provider selection. No inherit/clear tracking for this one (see
  // ChatSession.maxOutputTokens's own comment) — presence of a raw value is enough.
  let maxOutputTokens: number | undefined;
  if (provider.adapterType === "anthropic") {
    const maxOutputRaw = fields.maxOutputInput.value.trim();
    if (maxOutputRaw) {
      const parsed = Number(maxOutputRaw);
      if (!Number.isInteger(parsed) || parsed < 1) {
        showError(s.sessionSwitcher.maxOutputPositive);
        return null;
      }
      maxOutputTokens = parsed;
    }
  }

  let systemPrompt: string | undefined;
  if (controller.modes.systemPrompt === "inherit") {
    systemPrompt = undefined;
  } else if (controller.modes.systemPrompt === "clear") {
    systemPrompt = "";
  } else {
    systemPrompt = fields.systemPromptInput.value.trim();
  }

  return { model, temperature, maxContextTokens, maxOutputTokens, systemPrompt };
}

export function renderNewSessionSetup(
  contentEl: HTMLElement,
  providers: ProviderConfig[],
  openSessions: ChatSession[],
  handlers: ChatHandlers,
  container: HTMLElement,
  // Pre-selects this provider in the dropdown below — set when arriving here via
  // providerList.ts's "Start new chat" button (see ContentMode's own comment in chat.ts) rather
  // than the plain "+"/auto-default paths, neither of which have a specific provider in mind.
  // Only affects the initial `<option selected>`; ignored if it doesn't match any current
  // provider (e.g. deleted between clicking the button and this render).
  preselectedProviderId?: string,
  // Same idea as preselectedProviderId, for the Story Bible field below — set when this screen
  // re-renders itself after returning from Create/Edit (storyBibleFieldHtml's own buttons), so
  // the bible you just created/edited stays selected instead of resetting to "None". Plain "+"/
  // auto-default paths leave it unset, same as preselectedProviderId.
  preselectedStoryBibleId?: string
): void {
  const s = getStrings();
  const canCancel = openSessions.some((sess) => providers.some((p) => p.id === sess.providerId));
  const timestampExample = defaultTitle();

  contentEl.innerHTML = `
    <div class="chat-content-header">
      <h3>${s.sessionSwitcher.startNewChatTitle}</h3>
    </div>
    <label>${s.sessionSwitcher.chatNameOptional}
      <input type="text" id="new-session-name" placeholder="${escapeAttr(s.sessionSwitcher.chatNamePlaceholder(timestampExample))}" />
    </label>
    <label>${s.sessionSwitcher.provider}
      <select id="new-session-provider">
        ${providers
          .map(
            (p) =>
              `<option value="${p.id}"${p.id === preselectedProviderId ? " selected" : ""}>${escapeHtml(p.name)}</option>`
          )
          .join("")}
      </select>
    </label>
    <label>${s.sessionSwitcher.model}
      <div id="new-session-model-combobox"></div>
    </label>
    ${overrideFieldsHtml("new-session", {})}
    ${storyBibleFieldHtml("new-session", preselectedStoryBibleId)}
    <p id="new-session-error" class="error-text" style="display:none;"></p>
    <div class="form-actions">
      <button type="button" class="primary-btn" id="start-session-btn">${s.sessionSwitcher.startChat}</button>
      ${canCancel ? `<button type="button" class="link-btn" id="cancel-new-session-btn">${s.common.cancel}</button>` : ""}
    </div>
  `;

  wireInfoIcons(contentEl);

  const providerSelect = contentEl.querySelector("#new-session-provider") as HTMLSelectElement;
  const modelComboboxContainer = contentEl.querySelector(
    "#new-session-model-combobox"
  ) as HTMLElement;
  const errorEl = contentEl.querySelector("#new-session-error") as HTMLElement;
  const fields = queryOverrideFieldElements(contentEl, "new-session");
  const storyBibleSelect = contentEl.querySelector("#new-session-story-bible") as HTMLSelectElement;
  const storyBibleEditBtn = contentEl.querySelector(
    "#new-session-story-bible-edit-btn"
  ) as HTMLButtonElement;

  storyBibleSelect.addEventListener("change", () => {
    storyBibleEditBtn.disabled = !storyBibleSelect.value;
  });
  contentEl.querySelector("#new-session-story-bible-create-btn")?.addEventListener("click", () => {
    renderStoryBibleEditor(
      contentEl,
      undefined,
      (saved) =>
        renderNewSessionSetup(
          contentEl,
          providers,
          openSessions,
          handlers,
          container,
          providerSelect.value,
          saved.id
        ),
      () =>
        renderNewSessionSetup(
          contentEl,
          providers,
          openSessions,
          handlers,
          container,
          providerSelect.value,
          storyBibleSelect.value || undefined
        )
    );
  });
  contentEl.querySelector("#new-session-story-bible-edit-btn")?.addEventListener("click", () => {
    const bible = getStoryBibles().find((b) => b.id === storyBibleSelect.value);
    if (!bible) return;
    renderStoryBibleEditor(
      contentEl,
      bible,
      (saved) =>
        renderNewSessionSetup(
          contentEl,
          providers,
          openSessions,
          handlers,
          container,
          providerSelect.value,
          saved.id
        ),
      () =>
        renderNewSessionSetup(
          contentEl,
          providers,
          openSessions,
          handlers,
          container,
          providerSelect.value,
          storyBibleSelect.value || undefined
        )
    );
  });

  function showError(message: string): void {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }
  function hideError(): void {
    errorEl.style.display = "none";
  }

  let modelCombobox: ModelComboboxHandle;

  // A brand-new session has no saved values yet — every override field starts in "inherit"
  // (nothing to derive "override"/"clear" from, unlike Edit session's temperatureMode() etc.,
  // which read an existing session).
  const controller = createOverrideFieldsController(
    contentEl,
    fields,
    {
      temperature: "inherit",
      maxContext: "inherit",
      systemPrompt: "inherit",
    },
    {
      getModelCombobox: () => modelCombobox,
      getCurrentProvider: () => providers.find((p) => p.id === providerSelect.value),
      hideError,
    }
  );

  function mountComboboxFor(provider: ProviderConfig): void {
    modelCombobox = createModelCombobox(modelComboboxContainer, {
      initialValue: provider.model,
      placeholder: s.sessionSwitcher.modelPlaceholder,
      fetchModels: () =>
        listModels({
          endpoint: provider.endpoint,
          apiKey: provider.apiKey,
          adapterType: provider.adapterType,
        }),
      onError: showError,
      onErrorClear: hideError,
      onValueChange: () => controller.updatePlaceholders(provider),
    });
    controller.updatePlaceholders(provider);
  }

  const initialProvider = providers.find((p) => p.id === providerSelect.value) || providers[0];
  mountComboboxFor(initialProvider);

  providerSelect.addEventListener("change", () => {
    hideError();
    const provider = providers.find((p) => p.id === providerSelect.value);
    if (!provider) return;
    mountComboboxFor(provider);
    controller.applyProviderSwitchRules(provider);
  });

  contentEl.querySelector("#cancel-new-session-btn")?.addEventListener("click", () => {
    setContentMode(null);
    renderChat(container, handlers);
  });

  contentEl.querySelector("#start-session-btn")?.addEventListener("click", () => {
    hideError();
    const provider = providers.find((p) => p.id === providerSelect.value);
    if (!provider) return;

    const parsedFields = parseOverrideFields(
      modelCombobox,
      controller,
      fields,
      provider,
      s,
      showError
    );
    if (!parsedFields) return;
    const { model, temperature, maxContextTokens, maxOutputTokens, systemPrompt } = parsedFields;

    const nameInput = contentEl.querySelector("#new-session-name") as HTMLInputElement;
    const customName = nameInput.value.trim();

    const now = Date.now();
    const session: ChatSession = {
      id: genId(),
      providerId: provider.id,
      model,
      temperature,
      maxContextTokens,
      maxOutputTokens,
      systemPrompt,
      storyBibleId: storyBibleSelect.value || undefined,
      title: customName || defaultTitle(),
      messages: [],
      state: "open",
      createdAt: now,
      updatedAt: now,
    };
    saveSession(session);
    setContentMode({ kind: "session", sessionId: session.id });
    renderChat(container, handlers);
  });
}

// "Chat YYMMDD·HHMM" (e.g. "Chat 260812·1430") — the middle dot matches this app's existing
// separator convention (formatSessionTag's "Gemini · gemini-pro · Temperature: 0.7", the provider
// list's adapter/model line), and avoids a plain "." reading as one decimal number instead of
// date-then-time. The session switcher dropdown already prefixes every option with
// "${provider.name}: " (see renderSessionSwitcher above), so including the provider name here too
// showed up redundantly, e.g. "Gemini: Gemini chat". A plain "Chat" used to be the whole default
// (see this function's own git history), which meant every unrenamed session was
// indistinguishable in the switcher dropdown once you had more than one open — a timestamp gives
// each one a real identity without requiring a rename. Not the same thing as the removed
// maybeAutoTitle() (see CLAUDE.md/"Default session title") — this is computed once, at creation
// time, same as the old flat "Chat" was; a session's title still never changes on its own
// afterward, only via the pencil.
function defaultTitle(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const yy = pad(now.getFullYear() % 100);
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const min = pad(now.getMinutes());
  return `Chat ${yy}${mm}${dd}·${hh}${min}`;
}

// Relinks a session to a different (or newly re-added) provider, and/or to a different model on
// its current provider — the provider dropdown below includes the session's current provider as
// one of the options, so leaving it as-is and just picking a different model works too (a
// provider's own "model" field, edited via Provider Settings, is only ever the default for
// *new* sessions — it deliberately never retroactively changes an existing session's model, so
// this is the actual way to change one). Deleting a provider never deletes the sessions using it
// (they're independent records — see storage.ts), but re-adding "the same" provider gets a
// brand-new id (genId()), so it never auto-relinks; this is also the only way to reconnect an
// orphaned session. Mirrors renderNewSessionSetup's provider+model picker, plus the name field —
// this is the one screen for everything about a chat now that the standalone rename pencil was
// merged into it (2026-08-11, see the pencil button's own comment in renderSessionSwitcher above
// for why), and its override fields are now the same shared block renderNewSessionSetup uses
// (createOverrideFieldsController/overrideFieldsHtml above), so the two forms stay in sync.
function renderEditSessionForm(
  contentEl: HTMLElement,
  session: ChatSession,
  handlers: ChatHandlers,
  container: HTMLElement,
  // Same idea as renderNewSessionSetup's preselectedStoryBibleId — set when this screen
  // re-renders itself after returning from the Story Bible field's Create/Edit buttons, so the
  // bible you just created/edited stays selected instead of resetting to the session's saved
  // value. Defaults to the session's own storyBibleId, same as every other field here.
  preselectedStoryBibleId?: string
): void {
  const s = getStrings();
  const providers = getProviders();
  // A session whose provider was deleted has no matching <option> below — deliberately not
  // pre-selecting any real provider in that case (see the placeholder option), so the browser's
  // own "no option marked selected -> pick the first one" default can't silently pick an
  // unrelated provider. See CLAUDE.md's Story Bible field for the same "always give the
  // ambiguous/unset case its own explicit option" pattern this mirrors.
  const currentProviderMissing = !providers.some((p) => p.id === session.providerId);

  contentEl.innerHTML = `
    <div class="chat-content-header with-close-btn">
      <h3>${s.sessionSwitcher.editChatTitle}</h3>
      <button type="button" class="link-btn" id="edit-session-close-btn">${s.common.cancel}</button>
    </div>
    <label>${s.sessionSwitcher.chatName}
      <input type="text" id="edit-session-name" value="${escapeAttr(session.title)}" />
    </label>
    ${
      providers.length === 0
        ? `<p class="empty-hint">${s.sessionSwitcher.noProvidersHint}</p>`
        : `<label>${s.sessionSwitcher.provider}
             <select id="edit-session-provider-select">
               ${
                 currentProviderMissing
                   ? `<option value="" selected disabled>${s.sessionSwitcher.selectProviderPlaceholder}</option>`
                   : ""
               }
               ${providers
                 .map(
                   (p) =>
                     `<option value="${p.id}"${p.id === session.providerId ? " selected" : ""}>${escapeHtml(p.name)}</option>`
                 )
                 .join("")}
             </select>
           </label>
           <label>${s.sessionSwitcher.model}
             <div id="edit-session-model-combobox"></div>
           </label>
           ${overrideFieldsHtml("edit-session", session)}
           ${storyBibleFieldHtml("edit-session", preselectedStoryBibleId ?? session.storyBibleId)}`
    }
    <p id="edit-session-error" class="error-text" style="display:none;"></p>
    <div class="form-actions">
      ${providers.length > 0 ? `<button type="button" class="primary-btn" id="edit-session-save-btn">${s.common.save}</button>` : ""}
      <button type="button" class="link-btn" id="edit-session-cancel-btn">${s.common.cancel}</button>
    </div>
  `;

  wireInfoIcons(contentEl);

  contentEl.querySelector("#edit-session-cancel-btn")?.addEventListener("click", () => {
    renderChat(container, handlers);
  });
  contentEl.querySelector("#edit-session-close-btn")?.addEventListener("click", () => {
    renderChat(container, handlers);
  });

  if (providers.length === 0) return;

  const nameInput = contentEl.querySelector("#edit-session-name") as HTMLInputElement;
  const providerSelect = contentEl.querySelector(
    "#edit-session-provider-select"
  ) as HTMLSelectElement;
  const modelComboboxContainer = contentEl.querySelector(
    "#edit-session-model-combobox"
  ) as HTMLElement;
  const errorEl = contentEl.querySelector("#edit-session-error") as HTMLElement;
  const fields = queryOverrideFieldElements(contentEl, "edit-session");
  const storyBibleSelect = contentEl.querySelector(
    "#edit-session-story-bible"
  ) as HTMLSelectElement;
  const storyBibleEditBtn = contentEl.querySelector(
    "#edit-session-story-bible-edit-btn"
  ) as HTMLButtonElement;

  storyBibleSelect.addEventListener("change", () => {
    storyBibleEditBtn.disabled = !storyBibleSelect.value;
  });
  contentEl.querySelector("#edit-session-story-bible-create-btn")?.addEventListener("click", () => {
    renderStoryBibleEditor(
      contentEl,
      undefined,
      (saved) => renderEditSessionForm(contentEl, session, handlers, container, saved.id),
      () =>
        renderEditSessionForm(
          contentEl,
          session,
          handlers,
          container,
          storyBibleSelect.value || undefined
        )
    );
  });
  contentEl.querySelector("#edit-session-story-bible-edit-btn")?.addEventListener("click", () => {
    const bible = getStoryBibles().find((b) => b.id === storyBibleSelect.value);
    if (!bible) return;
    renderStoryBibleEditor(
      contentEl,
      bible,
      (saved) => renderEditSessionForm(contentEl, session, handlers, container, saved.id),
      () =>
        renderEditSessionForm(
          contentEl,
          session,
          handlers,
          container,
          storyBibleSelect.value || undefined
        )
    );
  });

  function showError(message: string): void {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }
  function hideError(): void {
    errorEl.style.display = "none";
  }

  let modelCombobox: ModelComboboxHandle;

  const controller = createOverrideFieldsController(
    contentEl,
    fields,
    {
      temperature: temperatureMode(session),
      maxContext: maxContextMode(session),
      systemPrompt: systemPromptMode(session),
    },
    {
      getModelCombobox: () => modelCombobox,
      getCurrentProvider: () => providers.find((p) => p.id === providerSelect.value),
      hideError,
    }
  );

  function mountComboboxFor(provider: ProviderConfig): void {
    modelCombobox = createModelCombobox(modelComboboxContainer, {
      // Only prefills the session's existing model when staying on the same provider — a
      // different provider's model names aren't interchangeable.
      initialValue: provider.id === session.providerId ? session.model : provider.model,
      placeholder: s.sessionSwitcher.modelPlaceholder,
      fetchModels: () =>
        listModels({
          endpoint: provider.endpoint,
          apiKey: provider.apiKey,
          adapterType: provider.adapterType,
        }),
      onError: showError,
      onErrorClear: hideError,
      onValueChange: () => controller.updatePlaceholders(provider),
    });
    controller.updatePlaceholders(provider);
  }

  // Unlike renderNewSessionSetup's own initialProvider (which can safely fall back to
  // providers[0] — there's no "original" provider a brand-new chat could be misrepresenting), an
  // orphaned session here must not silently mount some arbitrary provider's model list as if it
  // were already selected. Left unmounted (with a hint instead) until the author explicitly picks
  // one via the placeholder option above.
  const initialProvider = providers.find((p) => p.id === providerSelect.value);
  if (initialProvider) {
    mountComboboxFor(initialProvider);
  } else {
    modelComboboxContainer.innerHTML = `<p class="empty-hint">${s.sessionSwitcher.selectProviderFirstHint}</p>`;
  }

  providerSelect.addEventListener("change", () => {
    hideError();
    const provider = providers.find((p) => p.id === providerSelect.value);
    if (!provider) return;
    mountComboboxFor(provider);
    controller.applyProviderSwitchRules(provider);
  });

  contentEl.querySelector("#edit-session-save-btn")?.addEventListener("click", () => {
    hideError();

    const chatName = nameInput.value.trim();
    if (!chatName) {
      showError(s.sessionSwitcher.chatNameRequired);
      return;
    }

    const provider = providers.find((p) => p.id === providerSelect.value);
    if (!provider) {
      showError(s.sessionSwitcher.providerRequired);
      return;
    }

    const parsedFields = parseOverrideFields(
      modelCombobox,
      controller,
      fields,
      provider,
      s,
      showError
    );
    if (!parsedFields) return;
    const { model, temperature, maxContextTokens, maxOutputTokens, systemPrompt } = parsedFields;

    // Multi-window staleness guard (2026-08-11), same reasoning as chat.ts's sendUserMessage/
    // deleteMessagePair — this overwrites the whole session record, so if another window changed
    // it (e.g. sent a new message) since this form was opened, saving now would silently lose
    // that. Checked last, right before the actual mutation, after every other (cheap, local)
    // validation has already passed.
    const freshBeforeSave = getSession(session.id);
    if (!freshBeforeSave) {
      showError(s.sessionSwitcher.chatNoLongerExists);
      return;
    }
    if (freshBeforeSave.updatedAt !== session.updatedAt) {
      showError(s.sessionSwitcher.chatUpdatedElsewhere);
      return;
    }

    // Deliberately does NOT log a providerChanges entry here — see chat.ts's sendUserMessage
    // instead. Logging at save time would mark a switch that was never actually used (e.g.
    // Gemini → Kimi saved, no message sent, → Gemini saved again): two markers for a round trip
    // nothing was ever sent through. The divider is only meaningful once a message is actually
    // sent under the new provider, so that's where it's tracked.
    session.title = chatName;
    session.providerId = provider.id;
    session.model = model;
    session.temperature = temperature;
    session.maxContextTokens = maxContextTokens;
    session.maxOutputTokens = maxOutputTokens;
    session.systemPrompt = systemPrompt;
    session.storyBibleId = storyBibleSelect.value || undefined;
    session.updatedAt = Date.now();
    saveSession(session);
    renderChat(container, handlers);
  });
}
